import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, CheckCircle } from 'lucide-react';

const BUSINESS_TYPES = [
  { id: 'retail', name: 'General Retail', icon: '🛍️' },
  { id: 'liquor', name: 'Liquor Store', icon: '🍷' },
  { id: 'grocery', name: 'Grocery Store', icon: '🥬' },
  { id: 'convenience', name: 'Convenience Store', icon: '🏪' },
  { id: 'restaurant', name: 'Restaurant / QSR', icon: '🍔' },
  { id: 'cafe', name: 'Cafe / Coffee Shop', icon: '☕' },
];

export default function StoreSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings/features');
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = async (preset) => {
    setSaving(true);
    try {
      await api.post('/settings/preset', { preset });
      await loadSettings();
      toast.success('Business type updated');
    } catch (error) {
      toast.error('Failed to update business type');
    } finally {
      setSaving(false);
    }
  };

  const toggleFeature = async (feature) => {
    setSaving(true);
    try {
      await api.post('/settings/feature', { 
        feature, 
        enabled: !settings?.features?.[feature] 
      });
      await loadSettings();
      toast.success('Feature updated');
    } catch (error) {
      toast.error('Failed to update feature');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Store Settings</h1>
            <p className="text-muted-foreground">Manage your store features and preferences</p>
          </div>
          {saving && (
            <Badge variant="outline" className="gap-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Saving...
            </Badge>
          )}
        </div>
        
        {/* Business Type Presets */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Business Type</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">Select your business type to enable recommended features</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {BUSINESS_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => applyPreset(type.id)}
                  disabled={saving}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    settings?.business_type === type.id
                      ? 'border-cyan-500 bg-cyan-500/10'
                      : 'border-border hover:border-cyan-500/50'
                  }`}
                >
                  <span className="text-2xl mb-2 block">{type.icon}</span>
                  <span className="font-medium">{type.name}</span>
                  {settings?.business_type === type.id && (
                    <span className="text-xs text-cyan-400 block mt-1 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> Active
                    </span>
                  )}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Feature Flags */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Features</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <FeatureToggle
                label="AI Invoice Scanning"
                description="Upload and scan supplier invoices with AI"
                enabled={settings?.features?.ai_invoice_scanning}
                onToggle={() => toggleFeature('ai_invoice_scanning')}
                disabled={saving}
              />
              
              <FeatureToggle
                label="ID Verification"
                description="Scan IDs for age-restricted products"
                enabled={settings?.features?.id_verification}
                onToggle={() => toggleFeature('id_verification')}
                disabled={saving}
              />
              
              <FeatureToggle
                label="Weighted Items"
                description="Support for items sold by weight (lb, oz, kg)"
                enabled={settings?.features?.weighted_items}
                onToggle={() => toggleFeature('weighted_items')}
                disabled={saving}
              />
              
              <FeatureToggle
                label="Kitchen Display"
                description="Send orders to kitchen display system"
                enabled={settings?.features?.kitchen_display}
                onToggle={() => toggleFeature('kitchen_display')}
                disabled={saving}
              />
              
              <FeatureToggle
                label="Shelf Labels"
                description="Generate and print shelf price labels"
                enabled={settings?.features?.shelf_labels}
                onToggle={() => toggleFeature('shelf_labels')}
                disabled={saving}
              />
              
              <FeatureToggle
                label="Product Modifiers"
                description="Add-ons and customization options (restaurants)"
                enabled={settings?.features?.modifiers}
                onToggle={() => toggleFeature('modifiers')}
                disabled={saving}
              />
              
              <FeatureToggle
                label="AI Assistant"
                description="Get insights and recommendations from AI"
                enabled={settings?.features?.ai_assistant}
                onToggle={() => toggleFeature('ai_assistant')}
                disabled={saving}
              />
              
              <FeatureToggle
                label="Time Clock"
                description="Track employee hours and attendance"
                enabled={settings?.features?.time_clock}
                onToggle={() => toggleFeature('time_clock')}
                disabled={saving}
              />
              
              <FeatureToggle
                label="Delivery Integration"
                description="Manage orders from DoorDash, UberEats, etc."
                enabled={settings?.features?.delivery_integration}
                onToggle={() => toggleFeature('delivery_integration')}
                disabled={saving}
              />
              
              <FeatureToggle
                label="Multi-location"
                description="Manage multiple store locations"
                enabled={settings?.features?.multi_location}
                onToggle={() => toggleFeature('multi_location')}
                disabled={saving}
              />
            </div>
          </CardContent>
        </Card>

        {/* Receipt Settings */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Receipt Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Print Receipt Automatically</p>
                  <p className="text-sm text-muted-foreground">Print receipt after each sale</p>
                </div>
                <ToggleSwitch
                  enabled={settings?.receipt_settings?.auto_print}
                  onToggle={() => toggleFeature('receipt_auto_print')}
                  disabled={saving}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Email Receipt Option</p>
                  <p className="text-sm text-muted-foreground">Offer to email receipt to customers</p>
                </div>
                <ToggleSwitch
                  enabled={settings?.receipt_settings?.email_option}
                  onToggle={() => toggleFeature('receipt_email_option')}
                  disabled={saving}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Show Logo on Receipt</p>
                  <p className="text-sm text-muted-foreground">Print store logo on receipts</p>
                </div>
                <ToggleSwitch
                  enabled={settings?.receipt_settings?.show_logo}
                  onToggle={() => toggleFeature('receipt_show_logo')}
                  disabled={saving}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

function FeatureToggle({ label, description, enabled, onToggle, disabled }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border border-border/50 hover:bg-muted/30 transition-colors">
      <div>
        <p className="font-medium">{label}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <ToggleSwitch enabled={enabled} onToggle={onToggle} disabled={disabled} />
    </div>
  );
}

function ToggleSwitch({ enabled, onToggle, disabled }) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        enabled ? 'bg-cyan-500' : 'bg-muted'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-background transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}
